import React from 'react';
import Header from '@/components/common/Header';
import SEOHead from '@/components/common/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { WaitlistProvider } from '@/context/WaitlistContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import FooterSection from '@/components/homepage/FooterSection';
import { Compass, Sprout, Hourglass, PartyPopper } from 'lucide-react';

const About = () => {
  useAnalytics(); // This ensures page view tracking is active

  return (
    <WaitlistProvider>
      <SEOHead pageKey="about" />
      <div className="flex min-h-screen flex-col bg-[#F7F5F2] font-nunito">
      <Header showActions={true} />

      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="section-padding bg-[#F7F5F2] mb-0">
          <div className="section-container mb-0">
            <div className="flex flex-col items-center text-center">
              <h1 className="mb-8 text-4xl font-bold tracking-tight text-[#6C4F77] md:text-5xl lg:text-6xl max-w-4xl">
                The Story Behind Wabisabi (侘寂)
              </h1>
              <p className="mb-0 max-w-4xl text-[#2C2C2C] text-lg md:text-xl leading-relaxed" style={{ fontFamily: 'Euclid Circular A' }}>
                Why we believe no one should mess up showing they care
              </p>
            </div>
          </div>
        </section>

        {/* Personal Introduction with Image */}
        <section className="lg:py-0 px-4 lg:px-8 bg-[#F7F5F2]">
          <div className="section-container pt-0">
            <div className="mb-16">
              <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-[30px]">
                {/* Image Section - Mobile First */}
                <div className="lg:order-2 w-full lg:w-auto relative overflow-hidden lg:hidden" style={{
                  borderRadius: '8px',
                  width: '361px',
                  height: '353px',
                  border: '1px solid #2C2C2C'
                }}>
                  <img
                    src="/lovable-uploads/e4cd06c5-5cae-4d48-9fe2-6a809aa65e4a.png"
                    alt="Atinder - Founder of Wabisabi"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6C4F77]/10 to-transparent"></div>
                </div>

                {/* Content Section */}
                <div className="lg:w-2/3 w-full p-4 lg:p-8 md:lg:p-12 text-center lg:text-left lg:order-1">
                  <div className="max-w-2xl mx-auto lg:mx-0">
                    <h2 className="font-semibold text-[#2C2C2C] mb-4 text-[28px] lg:text-[60px]">
                      Meet Atinder
                    </h2>
                    <p className="leading-relaxed lg:leading-normal text-[#696969] text-[14px] lg:text-[22px]">
                      Hi, I'm Atinder, the founder of Wabisabi. I built Wabisabi out of a mix of frustration and realization.
                      <br /><br />
                      The realization that I'm not the only one who forgets the little things: the details that matter most to the people we love. And the frustration of not being able to show up the way I wanted to, with the right intent, at the right time.
                      <br /><br />
                      Wabisabi is my attempt to make care easier for everyone. A gentle way to hold onto what matters — and act on it, when it matters most.
                    </p>
                  </div>
                </div>

                {/* Image Section - Desktop */}
                <div className="hidden lg:block relative overflow-hidden lg:order-2" style={{
                  borderRadius: '8px',
                  width: '535px',
                  height: '440px',
                  border: '1px solid #2C2C2C'
                }}>
                  <img
                    src="/lovable-uploads/e4cd06c5-5cae-4d48-9fe2-6a809aa65e4a.png"
                    alt="Atinder - Founder of Wabisabi"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6C4F77]/10 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* It Started With Actually Remembering */}
        <section className="section-padding bg-[#60416D] relative overflow-hidden mx-[20px] md:mx-0 rounded-[8px] md:rounded-none mt-[20px] md:mt-0">
          <div className="relative z-10 mx-[20px] md:max-w-6xl md:mx-auto">
            <div className="flex flex-col text-center md:text-left">
              <div className="max-w-4xl mx-auto md:mx-0 md:px-8">
                <h2
                  className="font-bold text-white responsive-title leading-none mb-6 text-center md:text-left"
                  style={{
                    fontSize: '28px',
                    letterSpacing: '-1px',
                    verticalAlign: 'middle'
                  }}
                >
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      @media (min-width: 768px) {
                        .responsive-title {
                          font-size: 60px !important;
                          letter-spacing: -2px !important;
                        }
                      }
                    `
                  }} />
                  It Started With Actually Remembering
                </h2>

                <div className="mb-8 space-y-4" style={{ fontSize: '14px', fontFamily: 'Euclid Circular A', color: '#FFFFFF' }}>
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .content-text {
                      color: #FFFFFF !important;
                    }
                    @media (min-width: 768px) {
                      .content-text {
                        font-size: 1.125rem !important;
                        color: #FFFFFF !important;
                      }
                    }
                  `
                }} />
                <p className="content-text">
                  I've always cared deeply about the people in my life but I've also been terrible at remembering things. Not just dates or appointments (though I forget those too), but the little things: what my wife said she loved, what a friend was stressed about, what my sister was looking forward to. The details that make someone feel truly seen.
                </p>

                <p className="content-text">
                  Like most people, I tried. I gave gifts, checked in, showed up when I could, but it often felt like I was guessing. Or late. Or missing the moment entirely. Sound familiar?
                </p>

                <p className="content-text">
                  Then something small shifted everything.
                </p>

                <p className="content-text">
                  One day, my wife mentioned something she loved. Instead of relying on memory, I wrote it down. Later, when the moment was right, I showed up with exactly what she needed. Her reaction wasn't just joy, it was relief. Because it meant I had listened. It meant I remembered. And most of all, it meant she felt understood.
                </p>

                <p className="content-text">
                  That one gesture changed the way I approached care. I started capturing more of those moments, not just for gift ideas, but for check-ins, real conversations, birthdays, hard days, little wins. Not just for my wife, but for my friends, my family — the people who matter most.
                </p>

                <p className="content-text">
                  Bit by bit, things started to feel different. More connected. More intentional. More human.
                </p>

                <p className="content-text">
                  That's the heartbeat behind Wabisabi.
                </p>
                </div>
              </div>
            </div>

            {/* Image at bottom */}
            <div className="mt-8 w-full">
              <img
                src="/images/img5.jpg"
                alt="Wabisabi story"
                className="w-full object-cover"
                style={{
                  height: '353px',
                  border: '1px solid #2C2C2C',
                  borderRadius: '8px',
                  transform: 'scaleX(-1)'
                }}
              />
              <style dangerouslySetInnerHTML={{
                __html: `
                  @media (min-width: 768px) {
                    .mt-8 img {
                      height: 650px !important;
                    }
                  }
                `
              }} />
            </div>
          </div>
        </section>

        {/* What Wabisabi Means to Us */}
        <section className="section-padding bg-white relative overflow-hidden mx-[20px] md:mx-0 rounded-[8px] md:rounded-none mt-[20px] md:mt-0">
          <div className="relative z-10 mx-[20px] md:max-w-6xl md:mx-auto">
            <div className="text-center md:text-left">
              <div className="max-w-4xl mx-auto md:mx-0 md:px-8">
                <h2
                  className="font-bold text-black responsive-title leading-none mb-8"
                  style={{
                    fontSize: '28px',
                    letterSpacing: '-1px',
                    verticalAlign: 'middle'
                  }}
                >
                  <style dangerouslySetInnerHTML={{
                    __html: `
                      @media (min-width: 768px) {
                        .responsive-title {
                          font-size: 60px !important;
                          letter-spacing: -2px !important;
                        }
                      }
                    `
                  }} />
                  What Wabisabi Means to Us
                </h2>
              </div>

              <div className="max-w-4xl mx-auto md:mx-0 text-center md:text-left space-y-6 md:px-8" style={{ fontSize: '14px', fontFamily: 'Euclid Circular A' }}>
                <style dangerouslySetInnerHTML={{
                  __html: `
                    @media (min-width: 768px) {
                      .space-y-6 p {
                        font-size: 1.125rem !important;
                      }
                    }
                  `
                }} />
                <p className="text-[#2C2C2C] leading-relaxed">
                  Wabisabi is a Japanese philosophy that finds beauty in imperfection. And that's exactly how we see relationships — beautifully imperfect, worth nurturing, deserving of support.
                </p>

                <p className="text-[#2C2C2C] leading-relaxed">
                  You don't need a flawless memory to be a great friend, partner, or sibling. You just need to care — and sometimes, you need a little help remembering what matters most.
                </p>

                <p className="text-[#2C2C2C] leading-relaxed font-medium">
                  Wabisabi remembers what matters so you can focus on what matters most: being present, being genuine, being there for the people you love.
                </p>
                </div>
              </div>
            </div>
        </section>

        {/* Our Mission */}
        <section className="section-padding bg-[#F4978E] relative overflow-hidden mx-[20px] md:mx-0 rounded-[8px] md:rounded-none mt-[20px] md:mt-0">
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-38 h-38 bg-[#6C4F77]/3 rounded-full translate-x-19 -translate-y-19"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#F4978E]/4 rotate-12 -translate-x-16 translate-y-16"></div>

          <div className="section-container relative z-10 mx-[20px] md:mx-auto">
            <div className="section-title text-center animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <h2 className="text-3xl font-bold text-black md:text-4xl mb-6">Our Mission: Connection, Not Checklists</h2>
              <p className="mx-auto max-w-2xl text-[#2C2C2C] text-base md:text-xl mb-12" style={{ fontFamily: 'Euclid Circular A' }}>
                Building relationship intelligence, not a productivity tool
              </p>
            </div>

            <div className="max-w-7xl mx-auto">
              <div className="grid gap-2 grid-cols-1 md:grid-cols-4">
                <Card className="text-center bg-transparent border-none shadow-none">
                  <CardContent className="p-2 py-6 md:py-[50px] md:p-10">
                    <div className="mb-3">
                      <div className="text-black mb-2">
                        <Compass className="w-9 h-9" strokeWidth={1} />
                      </div>
                      <h3 className="font-semibold text-black text-left text-base md:text-[24px]">Notice</h3>
                    </div>
                    <p className="text-[#262729] text-sm md:text-[18px] leading-normal md:leading-tight text-left font-normal" style={{ fontFamily: 'Euclid Circular A', fontWeight: 400 }}>
                      Wabisabi helps you catch the small, fleeting moments and gently holds them for when you need them most.
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center bg-transparent border-none shadow-none">
                  <CardContent className="p-2 py-6 md:py-[50px] md:p-10">
                    <div className="mb-3">
                      <div className="text-black mb-2">
                        <Sprout className="w-9 h-9" strokeWidth={1} />
                      </div>
                      <h3 className="font-semibold text-black text-left text-base md:text-[24px]">Nurture</h3>
                    </div>
                    <p className="text-[#262729] text-sm md:text-[18px] leading-normal md:leading-tight text-left font-normal" style={{ fontFamily: 'Euclid Circular A', fontWeight: 400 }}>
                      You don't need to force closeness. Wabisabi helps relationships deepen naturally, over time, with presence, not pressure.
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center bg-transparent border-none shadow-none">
                  <CardContent className="p-2 py-6 md:py-[50px] md:p-10">
                    <div className="mb-3">
                      <div className="text-black mb-2">
                        <Hourglass className="w-9 h-9" strokeWidth={1} />
                      </div>
                      <h3 className="font-semibold text-black text-left text-base md:text-[24px]">Be Present</h3>
                    </div>
                    <p className="text-[#262729] text-sm md:text-[18px] leading-normal md:leading-tight text-left font-normal" style={{ fontFamily: 'Euclid Circular A', fontWeight: 400 }}>
                      This isn't a CRM for your best friend. Wabisabi nudges you to show up not just to check a box, but to actually be there.
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center bg-transparent border-none shadow-none">
                  <CardContent className="p-2 py-6 md:py-[50px] md:p-10">
                    <div className="mb-3">
                      <div className="text-black mb-2">
                        <PartyPopper className="w-9 h-9" strokeWidth={1} />
                      </div>
                      <h3 className="font-semibold text-black text-left text-base md:text-[24px]">Celebrate</h3>
                    </div>
                    <p className="text-[#262729] text-sm md:text-[18px] leading-normal md:leading-tight text-left font-normal" style={{ fontFamily: 'Euclid Circular A', fontWeight: 400 }}>
                      From remembering their coffee order to showing up on a tough day, Wabisabi helps you honor the quiet, beautiful imperfection of human connection.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div className="bg-[#F7F5F2] transition-all duration-1000 ease-in-out">
        <FooterSection />
      </div>
    </div>
    </WaitlistProvider>
  );
};

export default About;
